// Shared pieces of the Settings pages (owner, 2026-10-22: "work on settings like you do on online
// store"): tabs kept in `?tab=`, a compact card with an icon, a save bar that appears when a form has
// unsaved changes, the draft hook behind it and small inputs. Sizes follow the sidebar scale (D-042).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { t } from "@ady/i18n";
import { cx, Icon, type IconName } from "@ady/ui";
import { SectionBar } from "../shell/SectionBar";
import { frame } from "../marketing/ui";

/** The page's tab, read from and written to `?tab=` so links and reloads open the same tab. */
export function useTab<T extends string>(tabs: readonly T[], fallback: T): [T, (t: T) => void] {
  const read = () => {
    const q = new URLSearchParams(location.search).get("tab");
    return (tabs as readonly string[]).includes(q ?? "") ? (q as T) : fallback;
  };
  const [tab, setTab] = useState<T>(read);
  // A link to this page with another ?tab= (e.g. the Unverified badge, D-068) switches the tab
  // even when the page is already open. Keyed on each navigation (its history key), not only the
  // href: tab clicks below rewrite the URL with replaceState, which the router doesn't see.
  const nav = useRouterState({
    select: (s) => {
      const st = s.location.state as { key?: string; __TSR_key?: string } | undefined;
      return `${s.location.href}|${st?.__TSR_key ?? st?.key ?? ""}`;
    },
  });
  useEffect(() => {
    // The router's href, not window.location: the history entry can lag behind the router state.
    const q = new URL(nav.split("|")[0] ?? "", location.origin).searchParams.get("tab");
    setTab((tabs as readonly string[]).includes(q ?? "") ? (q as T) : fallback);
  }, [nav]); // tabs and fallback are the page's constants
  const change = (next: T) => {
    setTab(next);
    const url = new URL(location.href);
    if (next === fallback) url.searchParams.delete("tab");
    else url.searchParams.set("tab", next);
    history.replaceState(history.state, "", url);
  };
  return [tab, change];
}

export function SettingsTabs<T extends string>({
  label,
  value,
  onChange,
  items,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  items: { id: T; label: string; icon: IconName }[];
}) {
  return (
    <SectionBar label={label} value={value} onChange={(v) => onChange(v as T)} items={items} />
  );
}

/** A settings card: icon tile, title, an optional one-line help and something on the right. */
export function Panel({
  icon,
  title,
  help,
  aside,
  children,
  className,
  testId,
}: {
  icon: IconName;
  title: string;
  help?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <section
      className={cx(
        "flex min-w-0 flex-col gap-2.5 rounded-card border border-card bg-surface p-3 shadow-card",
        className,
      )}
      data-testid={testId}
    >
      <header className="flex items-start gap-2">
        <span className="ady-bg-brand-tint flex size-7 flex-none items-center justify-center rounded-xs">
          <Icon name={icon} gradient />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-sm font-semibold">{title}</h2>
          {help ? <span className="text-2xs text-fg-tertiary">{help}</span> : null}
        </span>
        {aside ? <span className="flex flex-none items-center gap-1.5">{aside}</span> : null}
      </header>
      {children}
    </section>
  );
}

/** The value as compared for "unsaved changes": the row version is not something the user edits. */
const key = (v: unknown): string =>
  JSON.stringify(v && typeof v === "object" && "version" in v ? { ...v, version: 0 } : (v ?? null));

/** A local copy of a saved value: edit it freely, compare with the server, put it back. */
export function useDraft<T>(server: T | undefined) {
  const [draft, setDraft] = useState<T | undefined>(server);
  const base = useRef<string>(key(server));
  const latest = useRef(server);
  latest.current = server;
  // Keyed on the content, so a caller may build `server` afresh on every render.
  const serverKey = key(server);
  useEffect(() => {
    const prev = base.current;
    base.current = serverKey;
    // Follow the server while nothing is being edited (a refetch must not wipe typing), and after a
    // save, when the draft now matches what came back.
    setDraft((cur) => (key(cur) === prev || key(cur) === serverKey ? latest.current : cur));
  }, [serverKey]);
  const dirty = draft !== undefined && key(draft) !== base.current;
  return {
    draft,
    set: (patch: Partial<T>) => setDraft((d) => (d === undefined ? d : { ...d, ...patch })),
    replace: setDraft,
    dirty,
    reset: () => setDraft(server),
  };
}

/** Appears at the bottom of the page while a form has unsaved changes. */
export function SaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
  error,
  testId,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  error?: string;
  testId?: string;
}) {
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    addEventListener("beforeunload", warn);
    return () => removeEventListener("beforeunload", warn);
  }, [dirty]);
  if (!dirty) return null;
  return (
    <div
      role="region"
      aria-label={t("settings.kit.unsaved")}
      className="sticky bottom-3 z-20 flex flex-wrap items-center gap-2 self-center rounded-card border border-card bg-surface px-3 py-2 shadow-3"
      data-testid={testId}
    >
      <span className="flex items-center gap-1.5 text-xs font-medium">
        <span className="size-2 rounded-full bg-warning-fg" aria-hidden="true" />
        {error ?? t("settings.kit.unsaved")}
      </span>
      <button
        type="button"
        onClick={onDiscard}
        disabled={saving}
        className="ady-hover-brand flex min-h-control items-center gap-1.5 rounded-xs border border-card px-3 text-sm font-medium text-fg-secondary"
      >
        <Icon name="undo" />
        <span className="ady-hover-label">{t("settings.kit.discard")}</span>
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        data-testid={testId ? `${testId}-save` : undefined}
        className="ady-bg-brand flex min-h-control items-center gap-1.5 rounded-xs px-3 text-sm font-medium text-on-primary disabled:opacity-50"
      >
        <Icon name="check" />
        {saving ? t("settings.kit.saving") : t("settings.kit.save")}
      </button>
    </div>
  );
}

/** A number box with a unit after it (days, points, %). */
export function UnitInput({
  value,
  onChange,
  unit,
  min,
  max,
  label,
  className,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  unit: string;
  min?: number;
  max?: number;
  label: string;
  className?: string;
}) {
  return (
    <span className={cx("relative flex items-center", className)}>
      <input
        type="number"
        inputMode="numeric"
        aria-label={label}
        className={cx(frame, "pe-14 tabular-nums")}
        value={value ?? ""}
        min={min}
        max={max}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : Math.trunc(Number(e.target.value)))
        }
      />
      <span className="pointer-events-none absolute end-2.5 text-2xs text-fg-tertiary">{unit}</span>
    </span>
  );
}

/** A small "how this works" note at the foot of a panel. */
export function Note({ icon = "info", children }: { icon?: IconName; children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 rounded-xs bg-sunken px-2.5 py-1.5 text-2xs text-fg-secondary">
      <span className="pt-px text-fg-tertiary">
        <Icon name={icon} />
      </span>
      <span>{children}</span>
    </p>
  );
}

/** A status dot with a label (Live, Off, Waiting …). */
export function Dot({
  tone,
  children,
}: {
  tone: "success" | "warning" | "neutral" | "danger";
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-1.5 text-2xs font-semibold text-fg-secondary">
      <span
        aria-hidden="true"
        className={cx(
          "size-1.5 rounded-full",
          tone === "success"
            ? "bg-success-fg"
            : tone === "warning"
              ? "bg-warning-fg"
              : tone === "danger"
                ? "bg-danger-fg"
                : "bg-fg-tertiary",
        )}
      />
      {children}
    </span>
  );
}
